/**
 * The client's copy of the world: what it has been told, and what it remembers.
 *
 * Two caches, and the difference between them is the whole idea:
 *
 * - **Tiles are remembered forever.** The server streams terrain once, as the
 *   player walks into it, and never re-sends it. So the client is the only
 *   thing that knows what the far side of the valley looked like — that is
 *   roguelike map memory, and it falls out of the AOI design rather than being
 *   bolted onto it.
 * - **Actors are forgotten immediately.** Anything outside the area of
 *   interest is gone from this table the moment the server says so. Keeping a
 *   stale actor around would draw a ghost, and worse, would mean the client
 *   held information the server had decided it should not have.
 */

import {
  DEFAULT_SKY,
  MILLITICKS_PER_TICK,
  MOVE_COST,
  MS_PER_TICK,
  rectContains,
  TILE_KINDS,
  type CarcassSnapshot,
  type FireSnapshot,
  type SignSnapshot,
  type CorpseSnapshot,
  type GraveSnapshot,
  type FloraSnapshot,
  type SatchelSnapshot,
  type EntitySnapshot,
  type Rect,
  type SelfSnapshot,
  type SkySnapshot,
  type Snapshot,
  type TileKind,
} from '@annwn/protocol';

/** Tile id 0 — what an unexplored cell reads as. */
const UNKNOWN = 0;

export interface RemoteEntity extends EntitySnapshot {
  /** When this position arrived, in `performance.now()` milliseconds. */
  readonly at: number;

  /** The position before it, for interpolation to work from. */
  readonly from: { x: number; y: number } | null;

  /**
   * How long this step should take to draw, in milliseconds — the *server's*
   * step duration, not the snapshot interval.
   *
   * Those differ, and assuming they were the same is what made remote walking
   * stutter: a snapshot arrives every 100 ms but a step takes two ticks, so
   * animating a step over one snapshot interval finishes it in half the time
   * and then holds. Matching the real duration makes consecutive steps join up
   * exactly — one animation ends as the next begins — which is what continuous
   * walking looks like.
   */
  readonly duration: number;
}

/**
 * How long a one-tile step takes to walk, in real milliseconds, from the
 * server's own cost table. Diagonals genuinely take longer (√2), and drawing
 * them at cardinal speed would make an actor arrive before it was allowed to.
 */
function stepDuration(dx: number, dy: number): number {
  const cost = dx !== 0 && dy !== 0 ? MOVE_COST.diagonal : MOVE_COST.cardinal;
  return (cost / MILLITICKS_PER_TICK) * MS_PER_TICK;
}

export class ClientWorld {
  readonly width: number;
  readonly height: number;

  /** Every tile ever delivered. Index is `y * width + x`. */
  private readonly tiles: Uint8Array;
  private readonly explored: Uint8Array;

  /** The last area of interest the server sent. Outside it, we are recalling. */
  private live: Rect | null = null;

  private readonly actors = new Map<string, RemoteEntity>();

  /** Bodies in range (M2-07). Replaced each snapshot; nothing to interpolate. */
  private corpses: readonly CorpseSnapshot[] = [];
  private graveList: readonly GraveSnapshot[] = [];

  /** Dead animals in range (M3-06) — what butchery is aimed at. */
  private carcassList: readonly CarcassSnapshot[] = [];

  /** Fires burning in range (M3-05). Replaced each snapshot, like the bodies. */
  private fireList: readonly FireSnapshot[] = [];
  private signList: readonly SignSnapshot[] = [];

  /** Plants in range (M3-03), indexed by tile for the renderer and the look cursor. */
  private plants = new Map<string, FloraSnapshot>();

  self: SelfSnapshot | null = null;
  tick = 0;
  ack = 0;

  /**
   * What the sky is doing (M3-05), and how far this viewer can see through it.
   *
   * Replaced wholesale each snapshot. Note that `sight` is the radius the
   * *server* culled by — the renderer draws the dark to exactly that edge, so
   * what the player sees ends where what they were sent ends.
   */
  sky: SkySnapshot = DEFAULT_SKY;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.tiles = new Uint8Array(width * height);
    this.explored = new Uint8Array(width * height);
  }

  inBounds(x: number, y: number): boolean {
    return x >= 0 && y >= 0 && x < this.width && y < this.height;
  }

  /** The stored tile id, or 0 for unexplored and off-map alike. */
  tileIdAt(x: number, y: number): number {
    if (!this.inBounds(x, y)) return UNKNOWN;
    return this.tiles[y * this.width + x] ?? UNKNOWN;
  }

  tileAt(x: number, y: number): TileKind {
    return TILE_KINDS[this.tileIdAt(x, y)] ?? 'void';
  }

  /** Has this tile ever been delivered? */
  isExplored(x: number, y: number): boolean {
    if (!this.inBounds(x, y)) return false;
    return this.explored[y * this.width + x] === 1;
  }

  /**
   * Is this tile currently within the server's area of interest?
   *
   * Explored-but-not-live is exactly the set the renderer dims: terrain the
   * character has seen and is not looking at.
   */
  isLive(x: number, y: number): boolean {
    return this.live !== null && rectContains(this.live, x, y);
  }

  get entities(): IterableIterator<RemoteEntity> {
    return this.actors.values();
  }

  entity(id: string): RemoteEntity | undefined {
    return this.actors.get(id);
  }

  /**
   * Whoever the client believes is standing on a tile.
   *
   * A belief, not a fact — the server decides, and a bump-attack aimed at a
   * tile is resolved against whoever is actually on it when the blow lands
   * (M2-01). This is only good enough to turn a blocked step into a swing,
   * which is exactly what it is for.
   *
   * A linear scan, like the sim's: at eight players it is a handful of
   * comparisons once a frame, and an index would be a second source of truth
   * that can disagree with the first.
   */
  actorAt(x: number, y: number): RemoteEntity | undefined {
    for (const actor of this.actors.values()) {
      if (actor.x === x && actor.y === y) return actor;
    }
    return undefined;
  }

  get population(): number {
    return this.actors.size;
  }

  /**
   * Bodies on the ground within the area of interest (M2-07).
   *
   * Replaced wholesale each snapshot rather than tracked like actors, because a
   * corpse does not move: there is nothing to interpolate, and a `gone` list
   * for something that never walks anywhere would be machinery earning nothing.
   */
  get bodies(): readonly CorpseSnapshot[] {
    return this.corpses;
  }

  /**
   * Graves within the area of interest (M4-07).
   *
   * Their own list beside `bodies`, and the difference is the ticket's: a
   * corpse is somebody's kit lying in the open and a grave is a permanent place
   * with a name on it. One of them will be gone by tomorrow evening and the
   * other will still be there in a month.
   */
  get graves(): readonly GraveSnapshot[] {
    return this.graveList;
  }

  /**
   * Dead animals on the ground (M3-06).
   *
   * Kept apart from `bodies` for the reason the sim keeps `Carcass` apart from
   * `Corpse`: one is a week's meat and the other is somebody's friend, and the
   * camp menu must never offer to butcher the second.
   */
  get carcasses(): readonly CarcassSnapshot[] {
    return this.carcassList;
  }

  /**
   * Fires burning on the ground within the area of interest (M3-05).
   *
   * Not derived from anything the client already had, because it could not be:
   * a fire lives in the sim's `lights` table rather than on the map, so until
   * the snapshot carried them a client had no way to know one was there at all.
   */
  get fires(): readonly FireSnapshot[] {
    return this.fireList;
  }

  /**
   * Plants on the ground within the area of interest (M3-03).
   *
   * Replaced wholesale each snapshot for the reason corpses are — a plant does
   * not move — and indexed by tile rather than kept as a list, because both
   * things that ask about it (the renderer drawing one cell, the look cursor
   * naming one tile) ask by coordinate.
   */
  floraAt(x: number, y: number): FloraSnapshot | undefined {
    return this.plants.get(`${x},${y}`);
  }

  get flora(): readonly FloraSnapshot[] {
    return [...this.plants.values()];
  }

  /**
   * Marks on the ground the viewer is close enough to notice (M3-08).
   *
   * Replaced wholesale each snapshot, like flora, and for a stronger version of
   * the same reason: what is culled here is *spot range*, which moves with the
   * player every step. A mark the client kept after walking away would be a
   * mark the player can still see from across the valley, which is exactly the
   * map-of-everything-that-moved the server culls to prevent.
   */
  get signs(): readonly SignSnapshot[] {
    return this.signList;
  }

  /** What the viewer is carrying (M3-03). Empty until the first snapshot. */
  get satchel(): SatchelSnapshot {
    return this.self?.satchel ?? { items: [], weight: 0 };
  }

  apply(snapshot: Snapshot, now: number): void {
    this.tick = snapshot.tick;
    this.ack = snapshot.ack;
    this.self = snapshot.self;
    this.sky = snapshot.sky ?? this.sky;

    for (const patch of snapshot.tiles) {
      for (let row = 0; row < patch.height; row += 1) {
        const y = patch.y + row;
        if (y < 0 || y >= this.height) continue;

        for (let column = 0; column < patch.width; column += 1) {
          const x = patch.x + column;
          if (x < 0 || x >= this.width) continue;

          const index = y * this.width + x;
          this.tiles[index] = patch.tiles[row * patch.width + column] ?? UNKNOWN;
          this.explored[index] = 1;
        }
      }
    }

    for (const id of snapshot.gone) this.actors.delete(id);

    for (const actor of snapshot.actors) this.track(actor, now);

    this.corpses = snapshot.corpses;
    this.graveList = snapshot.graves;
    this.carcassList = snapshot.carcasses ?? [];
    this.fireList = snapshot.fires ?? [];

    this.plants = new Map(snapshot.flora.map((patch) => [`${patch.x},${patch.y}`, patch]));
    this.signList = snapshot.signs ?? [];

    if (snapshot.self) this.live = this.aoiFromSnapshot(snapshot);
  }

  /**
   * Fold one actor's reported position into the interpolation state.
   *
   * The subtle case is the one where **nothing moved**. A snapshot arrives ten
   * times a second, but a step takes two ticks, so most snapshots repeat a
   * position the client already has. Those must leave the in-progress
   * transition — including its clock — completely alone. Restarting `at` on
   * every snapshot while keeping the old `from` pins the actor at the tile it
   * is walking *out of*: the elapsed time never gets far enough past the
   * interpolation delay to make progress, so it renders a step behind forever.
   * That was the bug this method exists in one piece to prevent.
   */
  private track(actor: EntitySnapshot, now: number): void {
    const previous = this.actors.get(actor.id);

    // Never seen before. Draw it where it is — a newly visible actor must not
    // slide in from wherever its predecessor happened to be standing.
    if (!previous) {
      this.actors.set(actor.id, { ...actor, at: now, from: null, duration: 0 });
      return;
    }

    // Unchanged: carry the transition and its clock forward untouched.
    if (previous.x === actor.x && previous.y === actor.y) {
      this.actors.set(actor.id, {
        ...actor,
        at: previous.at,
        from: previous.from,
        duration: previous.duration,
      });
      return;
    }

    const dx = actor.x - previous.x;
    const dy = actor.y - previous.y;

    // More than one tile in one update is not a walk — it is a correction, or
    // a spawn, or something we missed while out of range. Sliding across the
    // gap would draw the actor through whatever lies between.
    if (Math.abs(dx) > 1 || Math.abs(dy) > 1) {
      this.actors.set(actor.id, { ...actor, at: now, from: null, duration: 0 });
      return;
    }

    this.actors.set(actor.id, {
      ...actor,
      at: now,
      from: { x: previous.x, y: previous.y },
      duration: stepDuration(dx, dy),
    });
  }

  /**
   * Recover the server's area of interest from the snapshot.
   *
   * The rect is not on the wire — it is implied by the terrain patches, and
   * re-deriving it here beats adding a field that could disagree with the
   * tiles actually sent. Once the client has been walking a while the patches
   * are thin strips, so the last *full* rect is remembered rather than
   * recomputed from a one-column update.
   */
  private aoiFromSnapshot(snapshot: Snapshot): Rect | null {
    if (!snapshot.self) return this.live;

    const self = snapshot.self;
    const width = this.live?.width ?? 0;
    const height = this.live?.height ?? 0;

    if (width === 0 || height === 0) {
      // First snapshot: the single patch *is* the area of interest.
      const patch = snapshot.tiles[0];
      if (!patch) return null;
      return { x: patch.x, y: patch.y, width: patch.width, height: patch.height };
    }

    // Afterwards it simply follows the viewer, clamped to the map the same way
    // the server clamps it.
    const x = Math.max(0, Math.min(self.x - Math.floor(width / 2), this.width - width));
    const y = Math.max(0, Math.min(self.y - Math.floor(height / 2), this.height - height));
    return { x, y, width, height };
  }
}
